// SPDX-License-Identifier: AGPL-3.0-or-later

import {MessageFlags} from '@fluxer/constants/src/ChannelConstants';
import {MessageNotifications} from '@fluxer/constants/src/NotificationConstants';

export interface ReactionNotificationMentionSnapshot {
	mentionEveryone: boolean;
	mentionUserIds: ReadonlyArray<string>;
	mentionRoleIds: ReadonlyArray<string>;
	/** The original message's flag bitfield (checked for MessageFlags.SUPPRESS_NOTIFICATIONS). */
	flags: number;
}

export interface ReactionNotificationMentionContext {
	/** Guild-level "suppress @everyone/@here" override for the channel's guild. */
	everyoneMentionSuppressed: boolean;
	/** True when the reacted-to message lives in a guild channel (channel.guildId != null). */
	isGuildChannel: boolean;
	/**
	 * Role ids the current user holds in this guild. Null when the channel is a guild channel but
	 * the guild or the member record isn't available — matches the original behavior of returning
	 * false rather than falling through to the private-channel check.
	 */
	currentUserGuildRoleIds: ReadonlyArray<string> | null;
	/** Guild-level "suppress role mentions" override for the channel's guild. */
	roleMentionSuppressed: boolean;
	/** True when the channel is a private (DM/group DM) channel that is not muted. Only consulted when isGuildChannel is false. */
	isUnmutedPrivateChannel: boolean;
}

export interface ReactionNotificationEligibilityInput {
	currentUserId: string;
	reactorId: string;
	/** The reacted-to message's author id. Undefined/null (e.g. webhook messages) never matches currentUserId. */
	messageAuthorId: string | null | undefined;
	isBlocked: boolean;
	isDnd: boolean;
	streamerModeSuppressed: boolean;
	/** UserGuildSettings.resolvesToNoMessages(channel) — a mute or an explicit NO_MESSAGES level. Wins over every other flag. */
	channelMutesMessages: boolean;
	/** Account-level "notify me when people react to my own messages" setting. */
	notifyReactionsOwnMessages: boolean;
	/** Resolved (channel > category > guild > account) "notify me on reactions to messages that would have notified me" flag. */
	notifyReactionsNotifiedMessages: boolean;
	/** UserGuildSettings.resolveEffectiveMessageNotifications(channel) for the reacted-to message's channel. */
	notificationLevel: number;
	mentionSnapshot: ReactionNotificationMentionSnapshot;
	mentionContext: ReactionNotificationMentionContext;
}

function isMentionLike(
	currentUserId: string,
	snapshot: ReactionNotificationMentionSnapshot,
	context: ReactionNotificationMentionContext,
): boolean {
	if (snapshot.mentionEveryone && !context.everyoneMentionSuppressed) {
		return true;
	}
	if (snapshot.mentionUserIds.includes(currentUserId)) {
		return true;
	}
	if (context.isGuildChannel) {
		if (context.currentUserGuildRoleIds == null) return false;
		const roleIds = context.currentUserGuildRoleIds;
		return !context.roleMentionSuppressed && snapshot.mentionRoleIds.some((roleId) => roleIds.includes(roleId));
	}
	return context.isUnmutedPrivateChannel;
}

function wouldOriginalMessageNotify(input: ReactionNotificationEligibilityInput): boolean {
	const {flags} = input.mentionSnapshot;
	if ((flags & MessageFlags.SUPPRESS_NOTIFICATIONS) === MessageFlags.SUPPRESS_NOTIFICATIONS) {
		return false;
	}
	if (input.notificationLevel === MessageNotifications.NO_MESSAGES) {
		return false;
	}
	if (input.notificationLevel === MessageNotifications.ALL_MESSAGES) {
		return true;
	}
	return isMentionLike(input.currentUserId, input.mentionSnapshot, input.mentionContext);
}

/**
 * Pure eligibility decision for whether a MESSAGE_REACTION_ADD event should produce a
 * notification. Mirrors NotificationState#handleMessageReactionAdd in
 * `@app/features/ui/state/Notification`, minus all store/i18n access — every input here is
 * plain data the caller resolves from AccountManager/Relationships/LocalPresence/StreamerMode/
 * UserGuildSettings/UserSettings/Guilds/GuildMembers before calling in.
 */
export function resolveReactionNotificationEligibility(input: ReactionNotificationEligibilityInput): boolean {
	if (input.streamerModeSuppressed) return false;
	if (input.reactorId === input.currentUserId) return false;
	if (input.isBlocked) return false;
	if (input.isDnd) return false;
	if (input.channelMutesMessages) return false;

	const isOwnMessage = input.messageAuthorId != null && input.messageAuthorId === input.currentUserId;
	const ownMessageEligible = isOwnMessage && input.notifyReactionsOwnMessages;
	// Gated on !isOwnMessage, not !ownMessageEligible: this path covers reactions to messages the
	// user did not write, so turning off own-message reactions must not fall through to it.
	const othersMessageEligible =
		!isOwnMessage && input.notifyReactionsNotifiedMessages && wouldOriginalMessageNotify(input);

	return ownMessageEligible || othersMessageEligible;
}
